import type { SxProps, Theme } from "@mui/material/styles";

export interface QuickHintsStyleParams {
  isDark: boolean;
  isSmallScreen: boolean;
  isTabletScreen: boolean;
}

export const getHintLeadIconSx = ({
  isSmallScreen,
  isTabletScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  fontSize: isSmallScreen ? "0.5rem" : isTabletScreen ? "0.68rem" : "0.75rem",
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: isSmallScreen ? 8 : isTabletScreen ? 11 : 12,
  minWidth: isSmallScreen ? 8 : isTabletScreen ? 11 : 12,
  flexShrink: 0,
  transform: isSmallScreen
    ? "scale(0.8)"
    : isTabletScreen
      ? "scale(0.86)"
      : "none",
  transformOrigin: "center",
});

const PURPLE = "#6D28D9";
const PURPLE_DARK = "#5B21B6";
const CHILD_BG_LIGHT = "#F3E8FF";
const CHILD_BG_DARK = "rgba(67, 63, 80, 0.9)";

export const getHintButtonSx = (
  { isDark, isSmallScreen }: QuickHintsStyleParams,
  compact?: boolean,
): SxProps<Theme> => ({
  flex: compact ? "1 1 auto" : "0 1 auto",
  width: compact ? "100%" : "auto",
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: isSmallScreen ? 0.45 : 0.65,
  textAlign: "left",
  borderRadius: isSmallScreen ? "10px" : "14px",
  px: isSmallScreen ? 0.75 : 0.75,
  py: isSmallScreen ? 0.35 : compact ? 0.5 : 0.75,
  boxSizing: "border-box",
  backgroundColor: isDark ? "rgba(67, 63, 80, 0.9)" : "#EEE8F5",
  color: isDark ? "#E7DDFD" : "#6D28D9",
  border: "1px solid rgba(147, 112, 219, 0.28)",
  boxShadow: "none",
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    backgroundColor: isDark ? "rgba(73, 68, 88, 0.92)" : "#F4EEFA",
    borderColor: "rgba(109, 40, 217, 0.34)",
  },
  "&:active": {
    backgroundColor: isDark ? "rgba(78, 73, 94, 0.95)" : "#EAE2F3",
  },
});

export const getHintButtonContainerSx = (
  params: QuickHintsStyleParams,
  options?: { compact?: boolean; disabled?: boolean },
): SxProps<Theme> => {
  const { compact, disabled } = options ?? {};
  return {
    ...getHintButtonSx(params, compact),
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.45 : 1,
    ...(disabled
      ? {
          "&:hover": { backgroundColor: undefined, borderColor: undefined },
          "&:active": { backgroundColor: undefined },
        }
      : {}),
  };
};

export const getHintLabelSx = (
  { isSmallScreen }: QuickHintsStyleParams,
  compact?: boolean,
): SxProps<Theme> => ({
  fontSize: isSmallScreen ? "9.5px" : compact ? "11px" : "12px",
  fontWeight: 600,
  lineHeight: 1.28,
  color: "inherit",
  whiteSpace: compact ? "normal" : "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const getShellSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
  ...(isSmallScreen
    ? { px: 0.75, pt: 1, pb: 0.35 }
    : { px: 0.5, pt: 1, pb: 0.35 }),
});

export const getLevel2HeaderRowSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: isSmallScreen ? 0.5 : 0.75,
  mb: isSmallScreen ? 0.65 : 0.85,
  width: "100%",
});

export const getBackButtonSx = (
  { isSmallScreen }: QuickHintsStyleParams,
  disabled: boolean,
): SxProps<Theme> => ({
  display: "inline-flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  gap: 0.35,
  border: "none",
  borderRadius: "999px",
  px: isSmallScreen ? 1 : 1.25,
  py: isSmallScreen ? 0.45 : 0.55,
  minHeight: isSmallScreen ? 28 : 32,
  boxSizing: "border-box",
  backgroundColor: PURPLE,
  color: "#FFFFFF",
  boxShadow: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.45 : 1,
  transition: "background-color 0.15s ease, opacity 0.15s ease",
  "&:hover": disabled
    ? undefined
    : {
        backgroundColor: "#5B21B6",
      },
  "&:active": disabled
    ? undefined
    : {
        backgroundColor: "#4C1D95",
      },
});

export const getBackIconSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  fontSize: isSmallScreen ? 15 : 17,
  color: "#FFFFFF",
});

export const getBackLabelSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  fontSize: isSmallScreen ? "11px" : "13px",
  fontWeight: 600,
  lineHeight: 1.2,
  color: "#FFFFFF",
});

export const getSubtitleSx = ({
  isDark,
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  fontSize: isSmallScreen ? "10px" : "13px",
  fontWeight: 500,
  lineHeight: 1.35,
  color: isDark ? "rgba(231, 221, 253, 0.9)" : PURPLE_DARK,
  overflow: "hidden",
  textOverflow: "ellipsis",
  ml: 1,
});

export const getChildrenGridSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  display: "grid",
  gridTemplateColumns: isSmallScreen
    ? "repeat(2, minmax(0, 1fr))"
    : "repeat(4, minmax(0, 1fr))",
  gap: isSmallScreen ? 0.65 : 0.85,
  width: "100%",
});

export const getSkipButtonSx = (
  { isDark, isSmallScreen }: QuickHintsStyleParams,
  disabled: boolean,
): SxProps<Theme> => ({
  flexShrink: 0,
  border: "none",
  background: "none",
  padding: isSmallScreen ? "4px 2px" : "6px 4px",
  fontSize: isSmallScreen ? "11px" : "13px",
  fontWeight: 600,
  lineHeight: 1.2,
  color: isDark ? "rgba(231, 221, 253, 0.85)" : PURPLE,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.45 : 1,
  transition: "color 0.15s ease, opacity 0.15s ease",
  mr: 0.5,
  "&:hover": disabled
    ? undefined
    : {
        color: isDark ? "#E7DDFD" : PURPLE_DARK,
      },
});

export const getChildHintButtonSx = (
  { isDark, isSmallScreen }: QuickHintsStyleParams,
  disabled: boolean,
): SxProps<Theme> => ({
  width: "100%",
  minWidth: 0,
  minHeight: isSmallScreen ? 52 : 58,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: isSmallScreen ? 0.65 : 0.85,
  textAlign: "left",
  borderRadius: isSmallScreen ? "12px" : "14px",
  px: isSmallScreen ? 0.75 : 1,
  py: isSmallScreen ? 0.65 : 0.75,
  boxSizing: "border-box",
  backgroundColor: isDark ? CHILD_BG_DARK : CHILD_BG_LIGHT,
  color: isDark ? "#E7DDFD" : PURPLE_DARK,
  border: isDark
    ? "1px solid rgba(147, 112, 219, 0.28)"
    : "1px solid rgba(196, 181, 253, 0.55)",
  boxShadow: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.45 : 1,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": disabled
    ? undefined
    : {
        backgroundColor: isDark ? "rgba(73, 68, 88, 0.92)" : "#EDE4FF",
        borderColor: isDark
          ? "rgba(109, 40, 217, 0.34)"
          : "rgba(167, 139, 250, 0.75)",
      },
  "&:active": disabled
    ? undefined
    : {
        backgroundColor: isDark ? "rgba(78, 73, 94, 0.95)" : "#E9D5FF",
      },
});

export const getChildHintIconCircleSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: isSmallScreen ? 32 : 36,
  height: isSmallScreen ? 32 : 36,
  minWidth: isSmallScreen ? 32 : 36,
  borderRadius: "50%",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 1px 2px rgba(91, 33, 182, 0.08)",
});

export const getChildHintLeadIconSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  fontSize: isSmallScreen ? "1rem" : "1.125rem",
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

export const getChildHintLabelSx = ({
  isDark,
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  fontSize: isSmallScreen ? "10.5px" : "12px",
  fontWeight: 600,
  lineHeight: 1.22,
  color: isDark ? "#E7DDFD" : PURPLE_DARK,
  whiteSpace: "pre-line",
  textAlign: "left",
});

export const getMenuTitleSx = ({
  isDark,
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  fontSize: isSmallScreen ? "10px" : "12px",
  fontWeight: 600,
  lineHeight: 1.35,
  color: isDark ? "#E7DDFD" : "#4C1D95",
  mb: 0.65,
  px: 0.25,
});

export const getLevel1RowSx = ({
  isSmallScreen,
}: QuickHintsStyleParams): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: isSmallScreen ? "center" : "flex-start",
  gap: 0.5,
  width: "100%",
});
